import {
  Content,
  ContentPDF,
  ContentSlides,
  ContentType,
  ContentVideo,
  ContentLink,
  ContentLegacyGuide,
} from '../model/extras.domain';
import { PDFViewModel } from '../pdf/pdf.viewModel';
import { SlidesViewModel } from '../slides/slides.viewModel';
import { VideoViewModel } from '../video/video.viewModel';
import { LinkViewModel } from '../link/link.viewModel';
import { LegacyGuideViewModel } from '../legacy-guide/legacy-guide.viewModel';

type CreateContext = {
  parentId: string;
  openCreateModal: (contentType: ContentType) => void;
};

type EditContext = {
  content: Content;
  contentId: string;
  roleLite: string | null;
  openEditModal: (contentType: ContentType, contentId: string) => void;
};

type ContentActionRegistry = {
  create: (contentType: ContentType, ctx: CreateContext) => unknown;
  edit: (ctx: EditContext) => unknown;
};

type ContentActionDeps = {
  pdfViewModel: PDFViewModel;
  slidesViewModel: SlidesViewModel;
  videoViewModel: VideoViewModel;
  linkViewModel: LinkViewModel;
  legacyGuideViewModel: LegacyGuideViewModel;
};

export const createContentActionRegistry = (
  deps: ContentActionDeps,
): ContentActionRegistry => {
  const createHandlers: Partial<
    Record<ContentType, (ctx: CreateContext) => unknown>
  > = {
    ContentLink: ({ parentId }) =>
      deps.linkViewModel.dispatch('PrepareCreate', { parentId }),
    ContentPDF: ({ parentId }) =>
      deps.pdfViewModel.dispatch('PrepareCreate', { parentId }),
    ContentSlides: ({ parentId }) =>
      deps.slidesViewModel.dispatch('PrepareCreate', { parentId }),
    ContentVideo: ({ parentId }) =>
      deps.videoViewModel.dispatch('PrepareCreate', { parentId }),
    // ContentLegacyGuide: Cannot be created (legacy)
    // ContentTab: Uses simple modal (handled by openCreateModal fallback)
    // ContentImage: Created by Slides ViewModel
  };

  const editHandlers: Partial<
    Record<
      ContentType,
      (ctx: Required<Pick<EditContext, 'content' | 'roleLite'>>) => unknown
    >
  > = {
    ContentLink: ({ content }) =>
      deps.linkViewModel.dispatch(
        'Edit',
        content.value as ContentLink['value'],
      ),
    ContentLegacyGuide: ({ content, roleLite }) => {
      // Legacy guides can only be edited by non-users
      if (roleLite === 'user') return;
      return deps.legacyGuideViewModel.dispatch(
        'Edit',
        content.value as ContentLegacyGuide['value'],
      );
    },
    ContentPDF: ({ content }) =>
      deps.pdfViewModel.dispatch('Edit', content.value as ContentPDF['value']),
    ContentSlides: ({ content }) =>
      deps.slidesViewModel.dispatch(
        'Edit',
        content.value as ContentSlides['value'],
      ),
    ContentVideo: ({ content }) =>
      deps.videoViewModel.dispatch(
        'Edit',
        content.value as ContentVideo['value'],
      ),
    // ContentTab: Uses simple modal (handled by openEditModal fallback)
    // ContentImage: Edited through Slides ViewModel
  };

  const create = (contentType: ContentType, ctx: CreateContext) => {
    const handler = createHandlers[contentType];
    if (handler) return handler(ctx);

    // Fallback for ContentTab (simple modal without RHF)
    ctx.openCreateModal(contentType);
    return undefined;
  };

  const edit = (ctx: EditContext) => {
    const { content } = ctx;
    if (!content) return;

    const handler = editHandlers[content.type];
    if (handler) return handler({ content, roleLite: ctx.roleLite });

    // Fallback for ContentTab (simple modal without RHF)
    ctx.openEditModal(content.type, ctx.contentId);
    return undefined;
  };

  return { create, edit };
};

export type { ContentActionRegistry, CreateContext, EditContext };
